/**
 * Terminal spare-stock coverage — TRBUD-406.
 *
 * A manager says what each terminal should keep on the shelf for its trucks:
 * "Steer tire 295/75R22.5, 2 per truck". For each terminal the coverage then
 * reads: 3 operational trucks x 2 = 6 needed, 4 available, short 2.
 *
 * Eden's rulings, 22 October 2026:
 *  * 54. Operational means in the fleet and not parked, so a down truck counts;
 *    "available now" is shown beside it.
 *  * 55. One quantity per rule for the whole fleet. No per-terminal override.
 *  * 56. Only stock on the trucks' home-base location row counts. Stock anywhere
 *    else is listed apart, as "not tied to a terminal".
 *
 * Request bodies, wire shapes and two pure rules. Browser-safe.
 */
import { z } from "zod";
import { assetClasses, type AssetClass } from "./vocabulary.js";
import { idempotencyKey, quantityString, strictObject } from "./requests.js";

/** `spare_stock_rule_name_chk`'s upper bound. */
export const SPARE_STOCK_RULE_NAME_MAX = 200;
/** How many interchangeable parts one rule may name. */
export const SPARE_STOCK_RULE_PARTS_MAX = 50;

const ruleName = z.string().trim().min(1, "Give the spare a name.").max(SPARE_STOCK_RULE_NAME_MAX);

const memberPartIds = z
  .array(z.string().uuid())
  .min(1, "Pick at least one part that counts as this spare.")
  .max(SPARE_STOCK_RULE_PARTS_MAX)
  .refine((ids) => new Set(ids).size === ids.length, "A part is listed twice.");

/**
 * `POST /parts/spare-stock-rules` — `manageSpareStock`.
 *
 * Every part must carry the same unit, and the rule takes it; a part with
 * another unit, or none, is a 422 naming it.
 */
export const createSpareStockRuleBody = strictObject({
  name: ruleName,
  assetClass: z.enum(assetClasses),
  /** Spares per operational unit, in the members' unit. */
  quantityPerUnit: quantityString,
  partIds: memberPartIds,
  idempotencyKey,
});
export type CreateSpareStockRuleBody = z.infer<typeof createSpareStockRuleBody>;

/**
 * `PATCH /parts/spare-stock-rules/:id` — `manageSpareStock`.
 *
 * Only the fields sent change. `partIds` REPLACES the member set, and the rule
 * takes the new members' unit. `active: false` switches the rule off: rules are
 * never deleted.
 */
export const updateSpareStockRuleBody = strictObject({
  name: ruleName.optional(),
  assetClass: z.enum(assetClasses).optional(),
  quantityPerUnit: quantityString.optional(),
  active: z.boolean().optional(),
  partIds: memberPartIds.optional(),
  idempotencyKey,
}).refine(
  (body) =>
    body.name !== undefined ||
    body.assetClass !== undefined ||
    body.quantityPerUnit !== undefined ||
    body.active !== undefined ||
    body.partIds !== undefined,
  "Nothing to change.",
);
export type UpdateSpareStockRuleBody = z.infer<typeof updateSpareStockRuleBody>;

/** What a write did. */
export interface SpareStockRuleReceipt {
  ruleId: string;
  name: string;
  active: boolean;
  unitOfMeasure: string;
  partIds: string[];
}

/**
 * How a unit's home base was decided, folded to what a reader of the panel must
 * know: was it stated, or is it an estimate?
 *
 * `asset.domicile_basis` carries five values. Three are assertions (`own_lot`,
 * `unit_suffix`, `manual`) and two are inferences (`vendor_site`,
 * `nearest_estimate`) — see the column's own comment. `unrecorded` is a home
 * base with no basis at all, which the schema allows on purpose.
 */
export type HomeBaseBasisKind = "stated" | "estimated" | "unrecorded";

export function homeBaseBasisKind(basis: string | null): HomeBaseBasisKind {
  if (basis === null) return "unrecorded";
  if (basis === "own_lot" || basis === "unit_suffix" || basis === "manual") return "stated";
  // `vendor_site`, `nearest_estimate`, and anything the CHECK may admit later:
  // an unknown way of deciding is read as an estimate, never as a stated fact.
  return "estimated";
}

/** One unit the coverage counted. Parked and out-of-fleet units are never listed. */
export interface SpareStockUnit {
  assetId: string;
  unitLabel: string;
  /** `assetAvailability`'s answer: available counts as "available now". */
  availability: "available" | "down";
  /** Null for a unit with no home base. */
  homeBaseBasis: HomeBaseBasisKind | null;
}

/** One member part. */
export interface SpareStockMember {
  partId: string;
  partNumber: string;
  description: string;
  unitOfMeasure: string | null;
  /**
   * False when the part is retired or its unit no longer matches the rule's.
   * Its stock is then left out of every figure, and `notCountedReason` says why.
   */
  counted: boolean;
  notCountedReason: string | null;
}

/** One member part's stock at one location. Quantities to 3 places. */
export interface SpareStockPartStock {
  partId: string;
  partNumber: string;
  onHand: string;
  available: string;
}

/** A terminal: a location that is the home base of an in-fleet unit of the rule's class. */
export interface SpareStockTerminalRow {
  locationId: string;
  locationName: string;
  /** Available plus down units based here (decision 54). */
  operationalUnits: number;
  availableNow: number;
  /** Of the operational units, how many have an estimated (or unrecorded) home base. */
  estimatedUnits: number;
  /** operationalUnits x the rule's quantity. */
  target: string;
  /** Counted members' stock on this location row only (decision 56). */
  onHand: string;
  available: string;
  /** max(0, target - available). */
  short: string;
  units: SpareStockUnit[];
  stock: SpareStockPartStock[];
}

/** Operational units of the class that have no home base: no shelf can be counted for them. */
export interface SpareStockNoHomeBase {
  operationalUnits: number;
  availableNow: number;
  target: string;
  units: SpareStockUnit[];
}

/** Counted members' stock on a location that is no unit's home base. Never a custody location. */
export interface SpareStockUntiedRow {
  locationId: string;
  locationName: string;
  onHand: string;
  available: string;
  stock: SpareStockPartStock[];
}

export interface SpareStockCoverage {
  terminals: SpareStockTerminalRow[];
  noHomeBase: SpareStockNoHomeBase;
  untiedStock: SpareStockUntiedRow[];
}

export interface SpareStockRuleView {
  ruleId: string;
  name: string;
  assetClass: AssetClass;
  quantityPerUnit: string;
  unitOfMeasure: string;
  active: boolean;
  updatedAt: string;
  members: SpareStockMember[];
  /** Null for a switched-off rule: nothing is counted for it. */
  coverage: SpareStockCoverage | null;
}

/** `GET /parts/spare-stock` — `viewParts`. Current stock only; no date, vendor or category filter. */
export interface SpareStockResponse {
  rules: SpareStockRuleView[];
}

/**
 * Quantities as thousandths, so "1.500" + "2.250" cannot drift. Every figure
 * here is `numeric(12,3)`, whose largest value times 1000 is still exact in a
 * double.
 */
export function quantityToThousandths(value: string): number {
  return Math.round(Number(value) * 1000);
}

export function thousandthsToQuantity(milli: number): string {
  const sign = milli < 0 ? "-" : "";
  const abs = Math.abs(milli);
  return `${sign}${Math.floor(abs / 1000)}.${String(abs % 1000).padStart(3, "0")}`;
}

/** The shortfall the panel shows: never negative. */
export function spareShortfall(target: string, available: string): string {
  return thousandthsToQuantity(
    Math.max(0, quantityToThousandths(target) - quantityToThousandths(available)),
  );
}
